if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const activeButton = evt.submitter?.form === this.form ? evt.submitter : this.submitButton;
        if (this.submitting || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true'
          || activeButton.disabled || activeButton.getAttribute('aria-disabled') === 'true') return;

        if (!this.form.reportValidity()) return;
        let dbIntent;
        try { dbIntent = window.DBCommerce?.capture(this, evt.submitter); }
        catch (error) { this.handleErrorMessage(error.message); return; }
        this.error = false;
        this.querySelectorAll('[data-db-error]').forEach(e => e.hidden = true);
        this.handleErrorMessage();

        this.submitting = true;
        activeButton.setAttribute('aria-disabled', 'true');
        activeButton.setAttribute('aria-busy', 'true');
        activeButton.classList.add('loading');
        const activeSpinner = activeButton.querySelector('.loading__spinner, .loading-overlay__spinner');
        activeSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart && !dbIntent?.checkout) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        let mutationStarted = false;
        const task = () => Promise.resolve().then(async () => {
          if (!this.isConnected) throw new Error('The product form changed. Please try again.');
          if (window.DBCommerce) config.body = await window.DBCommerce.prepare(formData, dbIntent);
          if (!this.isConnected) throw new Error('The product form changed. Please try again.');
          mutationStarted = true;
          return window.DBCommerce ? window.DBCommerce.fetchResponse(`${routes.cart_add_url}`, config) : fetch(`${routes.cart_add_url}`, config);
        })
          .then(async (response) => {
            const body = await response.json();
            return response.ok ? body : { ...body, status: response.status };
          })
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }
            if (response.status) return;
            if (window.DBCommerce) response = await window.DBCommerce.ready(response, this.cart, dbIntent);
            if (dbIntent?.checkout) {
              const verifiedCart = await window.DBCommerce.commitDirectCheckout(dbIntent);
              this.resolveCartLinesUpdate(linesUpdateDeferred, verifiedCart);
              window.DBCommerce.navigate(window.DBCommerce.url('checkout'));
              return;
            }
            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            // Share only a read started after this add, never an older in-flight cart read.
            const verification = {
              version: this.cart.cartIntentVersion,
              revision: window.DBCommerce?.revision,
              fresh: !(window.DBCommerce?.pending > 1)
                && (typeof CartItems === 'undefined' || !CartItems.pendingCartDataPromise),
            };
            verification.promise = this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response, { verification });
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response, { verification });
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(e.message || window.cartStrings.error);
            if (mutationStarted) window.DBCommerce?.error(this, e.message);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitting = false;
            activeButton.classList.remove('loading');
            activeButton.removeAttribute('aria-busy');
            if (!this.error || activeButton !== this.submitButton) activeButton.removeAttribute('aria-disabled');
            activeSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
        return window.DBCommerce ? window.DBCommerce.run(task) : task();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.querySelectorAll('[data-db-buy-now]').forEach(button => button.disabled = disable);
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred, verifiedCart) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = verifiedCart?.currency && Array.isArray(verifiedCart.items)
          ? Promise.resolve(verifiedCart)
          : typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
        return pendingCartDataPromise;
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
